import { useState } from "react";
import type { AdminReviewListItemResponse } from "../../../types/adminTypes";
import { formatDateTime } from "../../../utils/DateUtils";
import Button from "../../ui/Button/Button";
import AdminPillBadge from "../ui/AdminPillBadge";
import AdminTableShell from "../ui/AdminTableShell";
import AdminReviewDetailModal from "./AdminReviewDetailModal";
import AdminReviewsFilters from "./AdminReviewsFilters";
import AdminReviewsTableRow from "./AdminReviewsTableRow";
import { adminReviewsColumns } from "./adminReviewsColumns";
import {
  getFullName,
  getReviewContextLabel,
  getReviewContextTone,
  getStatusTone,
} from "./adminReviewUtils";

type AdminReviewsTableProps = {
  reviews: AdminReviewListItemResponse[];
  isLoading: boolean;
  search: string;
  reviewContext: string;
  createdFromDate: string;
  createdToDate: string;
  pageSize: number;
  page: number;
  resultsCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onSearchChange: (value: string) => void;
  onReviewContextChange: (value: string) => void;
  onCreatedFromDateChange: (value: string) => void;
  onCreatedToDateChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
  onPageChange: (nextPage: number) => void;
};

const getReviewRowKey = (review: AdminReviewListItemResponse, index: number) => (
  [
    review.createdAt,
    review.gigTitle ?? "gig",
    review.reviewerEmail ?? review.reviewerFirstName ?? "reviewer",
    review.revieweeEmail ?? review.revieweeFirstName ?? "reviewee",
    review.reviewContext ?? "context",
    review.rating,
    index,
  ].join("|")
);

const AdminReviewsTable = ({
  reviews,
  isLoading,
  search,
  reviewContext,
  createdFromDate,
  createdToDate,
  pageSize,
  page,
  resultsCount,
  hasNextPage,
  hasPreviousPage,
  onSearchChange,
  onReviewContextChange,
  onCreatedFromDateChange,
  onCreatedToDateChange,
  onPageSizeChange,
  onPageChange,
}: AdminReviewsTableProps) => {
  const [selectedReview, setSelectedReview] = useState<AdminReviewListItemResponse | null>(null);

  return (
    <>
      <AdminTableShell
        columns={adminReviewsColumns}
        filters={
          <AdminReviewsFilters
            search={search}
            reviewContext={reviewContext}
            createdFromDate={createdFromDate}
            createdToDate={createdToDate}
            pageSize={pageSize}
            onSearchChange={onSearchChange}
            onReviewContextChange={onReviewContextChange}
            onCreatedFromDateChange={onCreatedFromDateChange}
            onCreatedToDateChange={onCreatedToDateChange}
            onPageSizeChange={onPageSizeChange}
          />
        }
        mobileCards={reviews.map((review, index) => {
          const reviewerName = getFullName(review.reviewerFirstName, review.reviewerLastName, "Unknown reviewer");
          const revieweeName = getFullName(review.revieweeFirstName, review.revieweeLastName, "Unknown reviewee");

          return (
            <article key={getReviewRowKey(review, index)} className="rounded-[1.5rem] border border-myteengig-border bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-myteengig-text-primary">{review.gigTitle ?? "Untitled gig"}</p>
                  <p className="mt-1 line-clamp-2 text-sm text-myteengig-text-muted">
                    {review.message?.trim() || "No written feedback was included."}
                  </p>
                </div>
                <AdminPillBadge>{review.rating.toFixed(1)} / 5</AdminPillBadge>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Review</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <AdminPillBadge tone={getReviewContextTone(review.reviewContext)}>
                      {getReviewContextLabel(review.reviewContext)}
                    </AdminPillBadge>
                    {review.gigRequestStatus ? <AdminPillBadge tone={getStatusTone(review.gigRequestStatus)}>{review.gigRequestStatus}</AdminPillBadge> : null}
                  </div>
                </div>
                <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">People</p>
                  <p className="mt-1 text-sm text-myteengig-text-primary">Reviewer: {reviewerName}</p>
                  <p className="mt-1 text-sm text-myteengig-text-muted">Reviewee: {revieweeName}</p>
                </div>
                <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Created</p>
                  <p className="mt-1 text-sm text-myteengig-text-primary">{formatDateTime(review.createdAt)}</p>
                </div>
                <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Gig Status</p>
                  <div className="mt-2">
                    {review.gigStatus ? <AdminPillBadge tone={getStatusTone(review.gigStatus)}>{review.gigStatus}</AdminPillBadge> : <AdminPillBadge>Unknown</AdminPillBadge>}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => setSelectedReview(review)}>
                  View
                </Button>
              </div>
            </article>
          );
        })}
        rows={reviews.map((review, index) => (
          <AdminReviewsTableRow key={getReviewRowKey(review, index)} review={review} onView={setSelectedReview} />
        ))}
        isLoading={isLoading}
        isEmpty={!isLoading && reviews.length === 0}
        emptyMessage="No reviews found for the current filters."
        page={page}
        pageSize={pageSize}
        resultsCount={resultsCount}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={onPageChange}
      />
      <AdminReviewDetailModal
        review={selectedReview}
        isOpen={selectedReview !== null}
        onClose={() => setSelectedReview(null)}
      />
    </>
  );
};

export default AdminReviewsTable;
